import type { FunctionComponent } from 'react';
import { Fragment, useState } from 'react';
import { Badge, Inline, mediaHelper, spacingHelper } from '@property.com.au/ui-kit';
import styled from 'styled-components';
import { WebLink } from '../../../../app-kit/components/WebLink/WebLink';
import { StarRating } from '../../../StarRating/StarRating';
import { trackAccordionInteraction } from '../../../../analytics/event-tracking/trackAccordionInteraction';
import {
  ChildcareAddress,
  ChildcareFactsSubtitle,
  ChildcareFactsTable,
  ChildcareFactsTitle,
  ChildcareName,
  ChildcareOpeningHours,
  DesktopChildcareFactsSubtitle,
  DesktopChildcareFactsSubtitleWrapper,
  LinksWrapper,
  StyledChildcareAccordionItem,
  StyledDivider,
  TitleWrapper,
  VacanciesStatusBadgeWrapper,
} from './ChildcareAccordionItem.styled';
import type { ChildcareCentre } from '../../types';
import { isStarRatingFact } from '../../types';

const MIN_SUBTITLE_LENGTH_TO_SHOW_TOOLTIP = 16;

const StyledWebLink = styled(WebLink)`
  /* NOTE: custom styling for desktop as cannot change sizeVariant prop dynamically without affecting SSR */
  ${mediaHelper('desktopMin')} {
    padding: ${spacingHelper('extraSmall large')};
  }
`;

export const ChildcareAccordionItem: FunctionComponent<ChildcareCentre> = ({
  name,
  vacancyBadge,
  openingHours,
  address,
  isDefaultOpen,
  childcareFacts,
  webLinks,
  tracking,
}) => {
  const [expanded, setExpanded] = useState(isDefaultOpen);
  return (
    <StyledChildcareAccordionItem
      id={`childcare-accordion-item-${name.replace(/\s/g, '-')}`}
      key={name}
      title={
        <TitleWrapper>
          <ChildcareName>{name}</ChildcareName>
          <DesktopChildcareFactsSubtitleWrapper>
            {childcareFacts.map((fact, index) => (
              <DesktopChildcareFactsSubtitle
                key={fact.title + fact.subtitle}
                index={index}
                title={
                  fact.subtitle.length >= MIN_SUBTITLE_LENGTH_TO_SHOW_TOOLTIP
                    ? fact.subtitle
                    : undefined
                }
                // This adds tooltip text for long subtitles that will overflow and be truncated
              >
                {isStarRatingFact(fact) ? (
                  <StarRating
                    percentage={fact.displayStarRatingPercentage}
                    accessibilityText={fact.accessibilityTitle}
                  >
                    {fact.subtitle}
                  </StarRating>
                ) : (
                  fact.subtitle
                )}
              </DesktopChildcareFactsSubtitle>
            ))}
          </DesktopChildcareFactsSubtitleWrapper>
        </TitleWrapper>
      }
      headingLevel={0} // Removes automatic wrapping of title prop as semantic heading
      defaultExpanded={isDefaultOpen}
      onToggle={(_expanded) => {
        setExpanded(!!_expanded);
        trackAccordionInteraction(_expanded ? tracking?.expand : tracking?.collapse);
      }}
      dangerouslySetClassNames={{
        header: 'header',
        headerButton: 'headerButton',
        contentWrapper: 'contentWrapper',
        content: 'content',
        textTitle: 'textTitle',
        chevronWrapper: 'chevronWrapper',
      }}
    >
      <VacanciesStatusBadgeWrapper>
        <Badge variant={vacancyBadge.variant}>{vacancyBadge.text}</Badge>
      </VacanciesStatusBadgeWrapper>

      {openingHours && <ChildcareOpeningHours>{openingHours}</ChildcareOpeningHours>}
      <ChildcareAddress>{address}</ChildcareAddress>
      <ChildcareFactsTable>
        {childcareFacts.map((fact, index) => (
          <Fragment key={fact.title}>
            {index !== 0 && <StyledDivider />}
            <Inline justifyContent="center" gap="0px" inset="0px" grow>
              <ChildcareFactsTitle>{fact.title}</ChildcareFactsTitle>
              <ChildcareFactsSubtitle>
                {isStarRatingFact(fact) ? (
                  <StarRating
                    percentage={fact.displayStarRatingPercentage}
                    accessibilityText={fact.accessibilityTitle}
                  >
                    {fact.subtitle}
                  </StarRating>
                ) : (
                  fact.subtitle
                )}
              </ChildcareFactsSubtitle>
            </Inline>
          </Fragment>
        ))}
      </ChildcareFactsTable>
      {webLinks && (
        <LinksWrapper>
          {webLinks.map((link) => (
            <StyledWebLink
              {...link}
              key={link.url}
              variant={link.variant}
              tracking={link.tracking}
              sizeVariant="small"
              {...(expanded ? {} : { tabIndex: -1 })}
              // This removes the web links from the tab order when the accordion item is not expanded aka. when the buttons are hidden.
            />
          ))}
        </LinksWrapper>
      )}
    </StyledChildcareAccordionItem>
  );
};
